/**
 * The help widget's knowledge base.
 *
 * Every answer here is a fixed string. Nothing is generated, so the widget
 * cannot invent a price, a policy or a delivery window — it either finds an
 * entry it is confident about or it hands off to a human. That property is the
 * whole point of doing this before any model work: a bot that quotes a wrong
 * wholesale rate costs more than no bot at all.
 *
 * `source` records where each fact was verified, so these can be re-checked
 * when the site changes. Facts taken from code beat facts taken from the policy
 * pages — the code is what actually charges the customer.
 *
 * `keywords` carry the matching weight; `asks` are phrasings real people use.
 * Both feed src/chat/match.js. Keep keywords lowercase and singular where
 * possible — the matcher stems crudely and does not know synonyms.
 */

export const KNOWLEDGE = [
  /* ------------------------------------------------------------- delivery */
  {
    id: "shipping-cost",
    title: "Shipping charges",
    // Verified in code, not from the policy page: Cart.js computes
    // `totalAmounts > 200 ? deliveryCharge : 50` and CartSlice sets
    // deliveryCharge to 0.
    source: "src/pages/CartPage/Cart.js:115, src/redux/CartSlice.js:40 — the Shipping page was corrected to match these on 2026-08-30",
    keywords: ["shipping", "delivery", "charge", "cost", "free", "postage", "courier", "fee"],
    asks: [
      "how much is shipping",
      "is delivery free",
      "what are the delivery charges",
      "do you charge for shipping",
    ],
    answer:
      "Delivery is free on orders above ₹200. On orders of ₹200 or less there is a ₹50 delivery charge. If you choose Cash on Delivery, ₹30 is added on top.",
    links: [{ label: "Shipping policy", to: "/shipping" }],
  },
  {
    id: "delivery-time",
    title: "Delivery time",
    source: "src/pages/Shipping/Shipping.js — Processing Time",
    keywords: ["how", "long", "delivery", "time", "days", "arrive", "dispatch", "ship", "when", "receive"],
    asks: [
      "how long does delivery take",
      "when will my order arrive",
      "how many days for delivery",
    ],
    answer:
      "Orders are processed and dispatched within 3 business days of payment. Delivery time after that depends on your location and the shipping method, and may be longer during festival periods.",
    links: [{ label: "Shipping policy", to: "/shipping" }],
  },
  {
    id: "cod",
    title: "Cash on Delivery",
    source: "src/pages/CartPage/Cart.js — COD option, +₹30",
    keywords: ["cod", "cash", "delivery", "pay", "payment", "online", "upi", "card"],
    asks: ["do you have cash on delivery", "can i pay on delivery", "what payment methods"],
    answer:
      "Yes, Cash on Delivery is available with a ₹30 COD charge. You can also pay online when you place the order.",
  },
  {
    id: "international",
    title: "International shipping",
    source: "src/pages/Shipping/Shipping.js — International Shipping",
    keywords: ["international", "abroad", "overseas", "outside", "india", "worldwide", "export", "country"],
    asks: ["do you ship internationally", "can you deliver outside india"],
    answer:
      "Yes, we ship worldwide. International orders may attract customs duties or import taxes in the destination country, which are payable by you. Message us on WhatsApp with your country and we'll confirm the cost before you order.",
    links: [{ label: "Shipping policy", to: "/shipping" }],
  },
  {
    id: "track",
    title: "Tracking an order",
    source: "src/pages/Shipping/Shipping.js — Shipment Tracking",
    keywords: ["track", "tracking", "status", "where", "order", "shipped", "dispatched"],
    asks: ["how do i track my order", "where is my order", "order status"],
    answer:
      "You'll get a confirmation with tracking details once your order ships. If you're signed in, your orders are listed under My Orders. For anything urgent, message us on WhatsApp with your order details.",
    links: [{ label: "My orders", to: "/myorders" }],
  },

  /* ------------------------------------------------- returns and problems */
  {
    id: "returns",
    title: "Returns and damaged items",
    // The page used to state three different windows (24h, 48h, 7 days). The
    // owner settled on 48 hours with photos on 2026-08-30 and the page was
    // rewritten to match, so this answer and the policy now agree.
    source: "src/pages/ReturnRefund/ReturnRefund.js",
    keywords: ["return", "refund", "exchange", "damaged", "broken", "wrong", "defect", "replace", "cancel", "unboxing", "video"],
    asks: [
      "what is your return policy",
      "my order arrived damaged",
      "can i return this",
      "i got the wrong item",
      "do i need an unboxing video",
    ],
    answer:
      "Please film yourself opening the parcel — claims are assessed from that video, and it can't be recorded once the box is open. If something arrives damaged, defective or incorrect, email us the video within 48 hours of delivery and we'll arrange an exchange, or a refund if we can't replace it. Refunds on online payments go back to the original method within 7 working days. We don't accept returns for change of mind, or on opened or used products.",
    links: [{ label: "Returns & refunds", to: "/returnrefund" }, { label: "Contact us", to: "/contact" }],
  },
  {
    id: "refund-time",
    title: "Refund timing",
    source: "src/pages/ReturnRefund/ReturnRefund.js — Refund Policy",
    keywords: ["refund", "money", "back", "long", "time", "credited", "reversal"],
    asks: ["how long does a refund take", "when will i get my money back"],
    answer:
      "Where a refund is approved on an online payment, it goes back to the original payment method within 7 working days of us receiving your request.",
    links: [{ label: "Returns & refunds", to: "/returnrefund" }],
  },

  /* ------------------------------------------------------------ wholesale */
  {
    id: "wholesale",
    title: "Wholesale and bulk orders",
    source: "src/pages/Wholesale/Wholesale.js — PRICE_TIERS, 50kg MOQ",
    keywords: ["wholesale", "bulk", "trade", "distributor", "reseller", "kg", "moq", "minimum", "quantity", "rate", "dealer", "supplier"],
    asks: [
      "do you sell wholesale",
      "what is the minimum order",
      "wholesale rates",
      "i want to buy in bulk",
      "bulk price per kg",
    ],
    answer:
      "Yes — factory direct, minimum order 50 kg. Rates are ₹170/kg for 50–99 kg, ₹160/kg for 100–299 kg, ₹150/kg for 300–599 kg, and ₹142/kg at 600 kg and above. Cases come in 5, 10, 20 and 50 kg, and mixed-fragrance cases are available.",
    links: [{ label: "Request a wholesale quote", to: "/wholesale" }],
  },
  {
    id: "private-label",
    title: "Private label",
    source: "Catalogue trade page; manufacturer is Achhari Perfumery Works",
    keywords: ["private", "label", "own", "brand", "oem", "white", "custom", "packaging", "manufacture"],
    asks: ["do you do private label", "can you manufacture under my brand", "oem"],
    answer:
      "Yes. We manufacture under our own brand and for others. Send us your volume and what you have in mind on WhatsApp or through the wholesale form and we'll come back with terms.",
    links: [{ label: "Wholesale enquiry", to: "/wholesale" }],
  },
  {
    id: "corporate-gifting",
    title: "Corporate and festival gifting",
    source: "src/data/Data.js — Diwali Hamper; wholesale funnel",
    keywords: ["corporate", "gift", "gifting", "hamper", "diwali", "bulk", "company", "employee", "festival"],
    asks: ["do you do corporate gifting", "diwali gift hampers in bulk"],
    answer:
      "Yes. We put together incense, dhoop and candle hampers for Diwali and corporate gifting, and can do volume orders with your own note or packaging. Tell us quantity and budget and we'll quote.",
    links: [{ label: "Wholesale enquiry", to: "/wholesale" }, { label: "Contact us", to: "/contact" }],
  },

  /* ------------------------------------------------------------- products */
  {
    id: "dhoop-vs-incense",
    title: "Dhoop sticks vs incense sticks",
    source: "src/seo/categories.js — incense-sticks / dhoop-sticks FAQs",
    keywords: ["difference", "dhoop", "incense", "agarbatti", "vs", "versus", "between", "compare", "which"],
    asks: [
      "what is the difference between dhoop and incense",
      "dhoop vs agarbatti",
      "which is better dhoop or incense",
    ],
    answer:
      "Incense sticks (agarbatti) are rolled on a thin bamboo core, so a bare stick is left after burning. Dhoop sticks have no bamboo — the whole stick is fragrant paste, so it burns away completely and gives a denser aroma with no bamboo smoke underneath the perfume.",
    links: [
      { label: "Dhoop sticks", to: "/dhoop-sticks" },
      { label: "Incense sticks", to: "/incense-sticks" },
    ],
  },
  {
    id: "cone-vs-stick",
    title: "Dhoop cones vs dhoop sticks",
    source: "src/seo/categories.js — dhoop-cone FAQ",
    keywords: ["cone", "cones", "stick", "difference", "dhoop", "compare", "which"],
    asks: ["what is a dhoop cone", "cone vs stick", "how are cones different"],
    answer:
      "Same paste, denser shape. A cone concentrates the same material into a smaller footprint, so it releases fragrance faster and more intensely than a stick of equal weight — better for a short ritual, where a stick suits a longer sitting. Burn cones on a metal plate or ceramic holder.",
    links: [{ label: "Dhoop cones", to: "/dhoop-cones" }],
  },
  {
    id: "sambrani-how",
    title: "Using a sambrani cup",
    source: "NT Sticks precautions artwork; src/seo/categories.js sambrani FAQ",
    keywords: ["sambrani", "havan", "cup", "cups", "light", "burn", "use", "charcoal", "kandi", "loban", "stand"],
    asks: [
      "how do i use a sambrani cup",
      "do sambrani cups need charcoal",
      "how to light havan cup",
    ],
    answer:
      "Always stand the cup on a holder or a metal plate first — it burns right through and the base gets hot enough to scorch a surface. Then hold a flame to the rim for a few seconds and it lights on its own; there's no separate charcoal or kandi needed. It burns for about 25 minutes. These cups give off a lot of smoke, so light one at a time in a room with some air, and keep them away from children while burning.",
    links: [{ label: "Sambrani & havan cups", to: "/sambrani-cups" }],
  },
  {
    id: "charcoal-free",
    title: "Charcoal-free and bamboo-free",
    source: "src/seo/categories.js; product descriptions in src/data/Data.js",
    keywords: ["charcoal", "bamboo", "natural", "chemical", "ingredient", "made", "safe", "smoke", "ash", "toxic"],
    asks: [
      "are your products charcoal free",
      "what are they made of",
      "do they have chemicals",
      "is it low smoke",
    ],
    answer:
      "Our dhoop is bound with natural material rather than charcoal powder, which is what keeps the smoke light and the ash pale instead of black. Dhoop sticks and cones have no bamboo core either, so there's no woodsmoke cutting across the fragrance. Everything is made at our own factory in Indore.",
  },
  {
    id: "burn-time",
    title: "How long they burn",
    source: "Confirmed by NT Sticks 2026-08-30: 25 min for dhoop sticks, cones and sambrani cups",
    keywords: ["long", "burn", "burns", "last", "lasts", "time", "minute", "duration", "hour"],
    asks: [
      "how long does a dhoop stick burn",
      "how long do the cones last",
      "burning time",
      "how long does a sambrani cup burn",
    ],
    answer:
      "About 25 minutes for dhoop sticks, dhoop cones and sambrani cups alike. Airflow makes a difference — in a draught they burn faster, in a closed room a little slower. Dhoop burns away completely, leaving only fine ash.",
    links: [{ label: "Dhoop sticks", to: "/dhoop-sticks" }, { label: "Dhoop cones", to: "/dhoop-cones" }],
  },
  {
    id: "fragrances",
    title: "Fragrances available",
    source: "src/data/Data.js — live catalogue",
    keywords: ["fragrance", "flavour", "flavor", "scent", "variety", "available", "options", "smell", "which", "list"],
    asks: ["what fragrances do you have", "which scents are available", "list of fragrances"],
    answer:
      "Dhoop sticks come in sandal, mogra, rose, loban, guggal, chameli, hawan, kasturi, oudh, lavender, rajnigandha, white musk, white gulab, kewda, ruh heena, muskan and more. Dhoop cones come in nine: lavender, amber, rose, muskan, ruh heena, sandal, mogra super, good morning and pineapple.",
    links: [{ label: "Browse the shop", to: "/shop" }],
  },
  {
    id: "price",
    title: "Prices",
    source: "src/data/Data.js — live catalogue prices",
    keywords: ["price", "cost", "rate", "how", "much", "rupee", "mrp", "cheap", "expensive"],
    asks: ["how much do they cost", "what is the price", "price list"],
    answer:
      "Most 100g jars of dhoop sticks and dhoop cones are ₹55 (MRP ₹80), with a case of five at ₹280. Small boxes start at ₹10. Sambrani cup boxes of 12 are ₹100 (MRP ₹120). Ganesh idols are ₹299. Exact prices are on each product page.",
    links: [{ label: "Browse the shop", to: "/shop" }, { label: "Download the catalogue", href: "/assets/docs/NT-Sticks-Catalogue.pdf" }],
  },
  {
    id: "catalogue",
    title: "Product catalogue",
    source: "public/assets/docs/NT-Sticks-Catalogue.pdf",
    keywords: ["catalogue", "catalog", "pdf", "brochure", "list", "download", "products"],
    asks: ["do you have a catalogue", "send me your product list", "pdf catalogue"],
    answer:
      "Yes — our full product catalogue is a free PDF download covering every range, with MRPs and wholesale rates.",
    links: [{ label: "Download the catalogue", href: "/assets/docs/NT-Sticks-Catalogue.pdf" }],
  },

  /* ------------------------------------------------------------ festivals */
  {
    id: "ganesh",
    title: "Eco-friendly Ganesh idols",
    source: "src/data/Data.js — Ganesh range",
    keywords: ["ganesh", "ganpati", "idol", "murti", "clay", "visarjan", "chaturthi", "eco", "pop", "plaster"],
    asks: [
      "do you sell ganesh idols",
      "are your ganesh idols eco friendly",
      "clay ganesh murti",
    ],
    answer:
      "Yes, during Ganesh Chaturthi. They're shaped from natural clay and nothing else — no plaster of Paris — so they dissolve properly at visarjan, including at home in a bucket or drum. Each idol is ₹299 and ships with a free chouki, mala, dhoop pack and red cloth. Both natural and hand-painted finishes are available.",
    links: [{ label: "Browse the shop", to: "/shop" }],
  },

  {
    id: "gst-invoice",
    title: "GST and invoices",
    // Added because the matcher was answering "what is the gst number" out of
    // the contact entry — both contain the word "number". A real question that
    // wholesale buyers ask deserves its own answer, even when that answer is
    // "ask us"; we deliberately do not print a GST number we cannot verify.
    source: "Business is Achhari Perfumery Works, Indore — GSTIN not in repo",
    keywords: ["gst", "gstin", "invoice", "bill", "billing", "tax", "hsn", "receipt", "b2b"],
    asks: ["what is your gst number", "do you give gst invoice", "can i get a tax invoice"],
    answer:
      "Yes, we can raise a GST invoice. Send your GSTIN and billing details along with your order or wholesale enquiry and we'll issue it — message us on WhatsApp or use the contact form.",
    links: [{ label: "Contact us", to: "/contact" }, { label: "Wholesale enquiry", to: "/wholesale" }],
  },

  /* -------------------------------------------------------------- company */
  {
    id: "contact",
    title: "Contact and location",
    source: "src/data/Data.js — footerTouch; Contact page",
    keywords: ["contact", "phone", "number", "call", "whatsapp", "email", "address", "location", "where", "factory", "shop", "visit", "indore"],
    asks: [
      "how do i contact you",
      "what is your phone number",
      "where are you located",
      "can i visit the factory",
    ],
    answer:
      "We're at 718, Loha Mandi, Panchshil Nagar, Indore, Madhya Pradesh 452001. Call or WhatsApp +91 62642 08349, or email ntincensesticks@gmail.com.",
    links: [{ label: "Contact page", to: "/contact" }],
  },
  {
    id: "about",
    title: "About NT Sticks",
    source: "Catalogue about page; Achhari Perfumery Works",
    keywords: ["who", "about", "company", "manufacturer", "brand", "since", "make", "factory", "nt", "sticks"],
    asks: ["who are you", "are you the manufacturer", "tell me about your company"],
    answer:
      "NT Sticks is the brand of Achhari Perfumery Works, Indore. We've been making dhoop and incense at our own factory since 2022 — mixing, rolling, pressing and packing all in-house, which is why we can sell factory direct with no distributor in between.",
  },
];

/** Suggested starter questions shown when the widget opens. */
export const SUGGESTED = [
  "How much is delivery?",
  "What's the difference between dhoop and incense?",
  "What are your wholesale rates?",
  "How do I use a sambrani cup?",
];
